import { createInfiniteQuery, createMutation, createQuery } from 'react-query-kit';

import { buildFilterUrl, withParameter } from 'support/helpers/urls/urls';
import { ImportDocumentsResponseDTO, IntegrationDTO, PaginationDTO } from 'support/types';
import { Filter } from 'support/types/filters';
import portalSchema from 'support/types/schema-portal';

import { createData, fetchData } from '../../http/http';

export type IntegrationsResponseDTO = PaginationDTO<IntegrationDTO>;

type GetIntegrationResponse =
  portalSchema.paths['/v1/integrations/{integrationId}']['get']['responses']['200']['content']['application/json'];

type GetIntegrationProcessesResponse =
  portalSchema.paths['/v1/integrations/{integrationId}/processes']['get']['responses']['200']['content']['application/json'];

export const useInfiniteIntegrations = createInfiniteQuery<IntegrationsResponseDTO, { query?: Filter }, Error>({
  primaryKey: '/integrations',
  queryFn: async ({ queryKey: [, variables], pageParam }) => {
    const urlParams = buildFilterUrl({ ...variables?.query, cursor: pageParam }, { forApiRequest: true });
    const { data } = await fetchData<IntegrationsResponseDTO>('/v1/integrations' + urlParams);

    return data;
  },
  getNextPageParam: (lastPage) => lastPage.nextCursor,
  getPreviousPageParam: (_, allPages) => allPages.at(-1)?.previousCursor,
});

export const useIntegration = createQuery<GetIntegrationResponse, { id?: string; withConfiguration?: boolean }, Error>({
  primaryKey: '/integrations',
  queryFn: async ({ queryKey: [, variables] }) => {
    const withParameters = [];
    if (variables.withConfiguration) {
      withParameters.push('configuration');
    }

    const { data } = await fetchData<IntegrationDTO>(
      '/v1/integrations/' + variables.id + withParameter(withParameters),
    );
    return data;
  },
  enabled: (data, variables) => !!variables.id,
});

export const useIntegrationProcesses = createQuery<GetIntegrationProcessesResponse, { id?: string }, Error>({
  primaryKey: '/integrations/processes',
  queryFn: async ({ queryKey: [, variables] }) => {
    const { data } = await fetchData<GetIntegrationProcessesResponse>(
      '/v1/integrations/' + variables.id + '/processes',
    );
    return data;
  },
  enabled: (data, variables) => !!variables.id,
});

type ImportDocumentsVariables = {
  integrationId: string;
  processId: string;
  documentNumbers: Array<string>;
};

export const useImportDocuments = createMutation<ImportDocumentsResponseDTO, ImportDocumentsVariables, Error>({
  mutationFn: async ({ integrationId, processId, documentNumbers }) => {
    const response = await createData<ImportDocumentsResponseDTO>(
      `/v1/integrations/${integrationId}/processes/${processId}/import-documents`,
      { document_numbers: documentNumbers },
    );

    return response.data;
  },
});
